import React from 'react';

import { FetchSharedViewResponse } from '../../../../../../../typings/api/skymap/rest/v0/.common';
import { includes } from '../../../../../utils/array-utils';
import { isDefined } from '../../../../../utils/variables';
import { AssetCategory } from '../../../../../viewer/elements/element-obj';
import { publish, SubscriptionTopic } from '../../../../../viewer/messaging/pubsub';
import { useSubscribe } from '../../../../hooks/use-subscribe';
import { useAssetsStore } from '../../state/assets-store';

const categoriesWithCheckBoxes: AssetCategory[] = ['area', 'cad', 'line', 'model', 'poi'];

type CheckedAssets = {
  area: string[];
  line: string[];
  cad: string[];
  model: string[];
  poi: string[];
};

type Props = {
  showOverlay: boolean;
  sharedView?: FetchSharedViewResponse['data'];
};

export const useAssetMenu = (props: Props) => {
  const { findByAssetId } = useAssetsStore();
  const [checkedAssetIds, setCheckedAssetIds] = React.useState<CheckedAssets>({
    area: [],
    line: [],
    cad: [],
    model: [],
    poi: [],
  });

  useSubscribe(SubscriptionTopic.AssetMenuCategoryCheckBoxesChanged, (message) => {
    if (includes(['area', 'line', 'cad', 'model', 'poi'], message.category)) {
      setCheckedAssetIds((oldValues) => ({
        ...oldValues,
        [message.category]: message.checkedAssetUuids,
      }));
    }
  });

  React.useEffect(() => {
    publish(
      props.showOverlay
        ? SubscriptionTopic.ShowAssetMenuOverlay
        : SubscriptionTopic.HideAssetMenuOverlay,
    );
    publish(SubscriptionTopic.SaveAssetMenuVisibilitySnapshot);

    if (props.sharedView) {
      const filterVisibleAssets = (assets: { id: string; visible: boolean }[]) => {
        const visibleAssets: string[] = [];
        for (const currentAsset of assets) {
          if (!currentAsset.visible) {
            continue;
          }
          const asset = findByAssetId(currentAsset.id);
          if (isDefined(asset) && asset.visible) {
            visibleAssets.push(asset.uuid);
          }
        }
        return visibleAssets;
      };

      publish(SubscriptionTopic.ShowAssetMenuCheckBoxes, {
        categories: categoriesWithCheckBoxes,
        autoCheckAssetIds: [
          ...props.sharedView.dynamicAssets.map((asset) => asset.id),
          ...(props.sharedView.pois ?? []).map((poi) => poi.id),
          ...props.sharedView.staticAssets.map((asset) => asset.id),
        ],
        disableAll: true,
      });

      publish(SubscriptionTopic.ShowOnlySpecificAssets, {
        assetUuids: [
          ...filterVisibleAssets(props.sharedView.dynamicAssets),
          ...filterVisibleAssets(props.sharedView.pois),
          ...filterVisibleAssets(props.sharedView.staticAssets),
        ],
      });
      return;
    }

    publish(SubscriptionTopic.ShowAssetMenuCheckBoxes, {
      categories: categoriesWithCheckBoxes,
      autoCheckVisibleAssets: true,
    });
  }, [props.sharedView, props.showOverlay, findByAssetId]);

  React.useEffect(() => {
    return () => {
      publish(SubscriptionTopic.RestoreAssetMenuVisibilitySnapshot);
      publish(SubscriptionTopic.HideAssetMenuOverlay);
      publish(SubscriptionTopic.HideAssetMenuCheckBoxes);
    };
  }, []);

  return {
    checkedAssetIds,
  };
};
